const telemetryStore = [];

const sleep = (ms) => new Promise((resolve) => {
  window.setTimeout(resolve, ms);
});

const pushTelemetry = (entry) => {
  telemetryStore.unshift(entry);
  if (telemetryStore.length > 300) {
    telemetryStore.length = 300;
  }
  console.info('[fetch-telemetry]', entry);
};

const isEmptyPayload = (value) => {
  if (value == null) return true;
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === 'object') {
    const data = value.data;
    if (Array.isArray(data)) return data.length === 0;
    if (data && typeof data === 'object') {
      if (Array.isArray(data.items)) return data.items.length === 0;
      return Object.keys(data).length === 0;
    }
    return Object.keys(value).length === 0;
  }
  return false;
};

const cloneFallbackValue = (value) => {
  if (Array.isArray(value)) return [...value];
  if (value && typeof value === 'object') return { ...value };
  return value;
};

const getRequestErrorMessage = (error) => {
  const detail = error?.response?.data?.detail;

  if (typeof detail === 'string' && detail.trim()) {
    return detail.trim();
  }
  if (detail && typeof detail === 'object' && typeof detail.message === 'string') {
    return detail.message;
  }
  if (typeof error?.message === 'string' && error.message.trim()) {
    return error.message.trim();
  }
  return 'Erro técnico';
};

const buildTelemetryEntry = ({
  startedAt,
  telemetry,
  status,
  attempts,
  timedOut = false,
  fallbackActivated = false,
  emptyPayload = false,
  httpStatus = null,
  errorMessage = null,
  required = false,
}) => ({
  module: telemetry?.module || 'unknown',
  endpoint: telemetry?.endpoint || 'unknown',
  durationMs: Math.round(performance.now() - startedAt),
  timedOut: Boolean(timedOut),
  fallbackActivated: Boolean(fallbackActivated),
  emptyPayload: Boolean(emptyPayload),
  status: status || 'ok',
  attempts,
  required: Boolean(required),
  httpStatus,
  errorMessage,
  timestamp: new Date().toISOString(),
});

const attachRequestMeta = (value, meta) => {
  if (!value || (typeof value !== 'object' && typeof value !== 'function')) {
    return value;
  }

  try {
    Object.defineProperty(value, '__safeMeta', {
      value: meta,
      enumerable: false,
      configurable: true,
      writable: true,
    });
  } catch {
    value.__safeMeta = meta;
  }

  if (meta.status !== 'ok' && typeof value.status === 'undefined') {
    try {
      value.status = meta.status;
    } catch {
      // ignore readonly objects
    }
  }

  return value;
};

export const getFetchTelemetrySnapshot = () => [...telemetryStore];

export const safeRequest = async (request, fallbackValue, options = {}) => {
  const {
    timeoutMs = 25000,
    onError,
    telemetry,
    retries = 0,
    retryDelayMs = 400,
    required = false,
  } = options;
  const startedAt = performance.now();

  const finalizeTelemetry = (payload) => {
    if (!telemetry) return;
    const entry = buildTelemetryEntry({ startedAt, telemetry, required, ...payload });
    pushTelemetry(entry);
    telemetry.onEvent?.(entry);
  };

  const totalAttempts = Math.max(0, retries) + 1;
  let lastError = null;

  for (let attempt = 1; attempt <= totalAttempts; attempt += 1) {
    try {
      const result = await Promise.race([
        Promise.resolve().then(() => (typeof request === 'function' ? request() : request)),
        new Promise((resolve) => {
          window.setTimeout(() => resolve({ __safe_timeout: true }), timeoutMs);
        }),
      ]);

      if (result?.__safe_timeout) {
        const timeoutError = new Error('request_timeout');
        timeoutError.__safeTimeout = true;
        throw timeoutError;
      }

      const successMeta = buildTelemetryEntry({
        startedAt,
        telemetry,
        status: 'ok',
        attempts: attempt,
        emptyPayload: isEmptyPayload(result),
        required,
      });
      finalizeTelemetry(successMeta);
      return attachRequestMeta(result, successMeta);
    } catch (error) {
      lastError = error;
      if (attempt < totalAttempts) {
        await sleep(retryDelayMs);
      }
    }
  }

  const failureMeta = buildTelemetryEntry({
    startedAt,
    telemetry,
    status: required ? 'error' : (lastError?.__safeTimeout ? 'timeout' : 'fallback'),
    attempts: totalAttempts,
    timedOut: Boolean(lastError?.__safeTimeout),
    fallbackActivated: !required,
    emptyPayload: isEmptyPayload(fallbackValue),
    httpStatus: lastError?.response?.status ?? null,
    errorMessage: getRequestErrorMessage(lastError),
    required,
  });

  if (lastError && typeof lastError === 'object') {
    lastError.__safeMeta = failureMeta;
  }

  onError?.(lastError);
  finalizeTelemetry(failureMeta);

  if (required && lastError) {
    throw lastError;
  }

  return attachRequestMeta(cloneFallbackValue(fallbackValue), failureMeta);
};